import { sin, cos } from '../../sim/math/trig.ts';
import { mulberry32 } from '../world.ts';
import type { RouteData } from '../../sim/types.ts';
import type { DecorationData } from '../decoration.ts';
import type { SeasonalDecor } from '../seasonal.ts';

/**
 * Halloween: jack-o'-lanterns on the guardrail posts, pumpkins piled on the
 * verges, a few graves on the straights, and bats circling the hairpins.
 *
 * Placed once from the route's seed, so every visit looks the same. Drawn in
 * the world's ink-and-paper style, with the pumpkins and their light as the
 * colour: on Akari's dark ground the lanterns are what the eye finds.
 */

const TAU = Math.PI * 2;

const PUMPKIN = '#e8742a';
const PUMPKIN_GROOVE = '#a9481a';
const STEM = '#55602a';
const LIGHT = '#ffc061';
const EARTH = '#3a3128';
const STONE = '#a29d92';
const STONE_TOP = '#c9c4b8';
const OUTLINE = '#141414';
const BAT = '#0b0b0b';
const BAT_RIM = 'rgba(244,241,234,0.8)';

/** Metres between pumpkin piles along the road. */
const PILE_EVERY = 110;
/** How many graveyards, spread down the route. */
const GRAVEYARDS = 4;
/** Corner marker severity from which a corner is a hairpin, and gets bats. */
const HAIRPIN = 5;

interface Pumpkin {
  index: number;
  x: number;
  y: number;
  r: number;
  rot: number;
  lit: boolean;
  phase: number;
}

interface Grave {
  index: number;
  x: number;
  y: number;
  /** Facing away from the road: the headstone is at the far end. */
  rot: number;
  rounded: boolean;
}

interface Bat {
  radius: number;
  /** rad/s round the roost; the sign is the direction. */
  speed: number;
  phase: number;
  flap: number;
}

interface Roost {
  index: number;
  x: number;
  y: number;
  bats: Bat[];
}

export function halloweenDecor(route: RouteData, deco: DecorationData | null): SeasonalDecor {
  const s = route.samples;
  const n = s.x.length;
  const ds = route.sampleSpacing;
  const rand = mulberry32(seedOf(route.id) ^ 0x3110);
  const pumpkins: Pumpkin[] = [];
  const graves: Grave[] = [];
  const roosts: Roost[] = [];

  /** A point `offset` metres left of sample i (negative: right). */
  const beside = (i: number, offset: number) => ({
    x: s.x[i] - sin(s.heading[i]) * offset,
    y: s.y[i] + cos(s.heading[i]) * offset,
  });
  // Clear of every stretch of road, not only the one it was placed beside: a
  // hairpin folds the road back on itself, and the verge of one leg can be the
  // tarmac of the next.
  const clear = (x: number, y: number, margin: number) => {
    for (let i = 0; i < n; i++) {
      const dx = x - s.x[i];
      const dy = y - s.y[i];
      const reach = s.halfWidth[i] + margin;
      if (dx * dx + dy * dy < reach * reach) return false;
    }
    return true;
  };

  // Jack-o'-lanterns, one on every guardrail post.
  const objects = deco && deco.routeId === route.id ? deco.objects : [];
  for (const o of objects) {
    if (o.kind !== 'post') continue;
    pumpkins.push({ index: o.index, x: o.x, y: o.y, r: 0.6 + rand() * 0.12, rot: rand() * TAU, lit: true, phase: rand() * TAU });
  }

  // Piles on the verges, now one side, now the other.
  for (let i = 20; i < n - 20; i += Math.round(PILE_EVERY / ds)) {
    const side = rand() < 0.5 ? 1 : -1;
    const c = beside(i, side * (s.halfWidth[i] + 2.4 + rand() * 1.6));
    if (!clear(c.x, c.y, 1.8)) continue;
    const count = 3 + Math.floor(rand() * 3);
    for (let k = 0; k < count; k++) {
      const a = rand() * TAU;
      const d = k === 0 ? 0 : 0.6 + rand() * 0.6;
      pumpkins.push({
        index: i,
        x: c.x + cos(a) * d,
        y: c.y + sin(a) * d,
        r: k === 0 ? 0.75 : 0.4 + rand() * 0.3,
        rot: rand() * TAU,
        lit: k === 0 && rand() < 0.6,
        phase: rand() * TAU,
      });
    }
  }

  // Graves, in short rows on the straights.
  const straight = (i: number) => {
    for (let j = Math.max(0, i - 12); j <= Math.min(n - 1, i + 12); j++) if (Math.abs(s.curvature[j]) > 1 / 150) return false;
    return true;
  };
  for (let q = 1; q <= GRAVEYARDS; q++) {
    const target = Math.floor((n * q) / (GRAVEYARDS + 1));
    let at = -1;
    for (let d = 0; d < n / (GRAVEYARDS + 1) / 2 && at < 0; d++) {
      if (target + d < n - 12 && straight(target + d)) at = target + d;
      else if (target - d > 12 && straight(target - d)) at = target - d;
    }
    if (at < 0) continue;
    const side = q % 2 === 0 ? 1 : -1;
    const h = s.heading[at];
    const row = beside(at, side * (s.halfWidth[at] + 6));
    const count = 4 + Math.floor(rand() * 3);
    for (let j = 0; j < count; j++) {
      const along = (j - (count - 1) / 2) * 1.8;
      const x = row.x + cos(h) * along;
      const y = row.y + sin(h) * along;
      if (!clear(x, y, 3)) continue;
      graves.push({ index: at, x, y, rot: h + (side * Math.PI) / 2 + (rand() - 0.5) * 0.25, rounded: rand() < 0.5 });
    }
    // A lantern at the end of the row, to find it by.
    const lamp = beside(at, side * (s.halfWidth[at] + 3.4));
    const end = (count / 2 + 0.6) * 1.8;
    pumpkins.push({ index: at, x: lamp.x + cos(h) * end, y: lamp.y + sin(h) * end, r: 0.45, rot: rand() * TAU, lit: true, phase: rand() * TAU });
  }

  // Bats over every hairpin, circling just inside the apex, so their loops
  // cross the road the car is on rather than the middle of the infield.
  for (const o of objects) {
    if (o.kind !== 'marker' || (o.severity ?? 0) < HAIRPIN) continue;
    let apex = o.index;
    for (let j = o.index; j < Math.min(n, o.index + 30); j++) if (Math.abs(s.curvature[j]) > Math.abs(s.curvature[apex])) apex = j;
    const k = s.curvature[apex];
    if (Math.abs(k) < 1e-3) continue;
    const inside = Math.min(1 / Math.abs(k), s.halfWidth[apex] + 5) * Math.sign(k);
    const c = beside(apex, inside);
    const bats: Bat[] = [];
    for (let b = 0; b < 3; b++) {
      bats.push({
        radius: 4 + rand() * 5,
        speed: (0.7 + rand() * 0.6) * (rand() < 0.5 ? 1 : -1),
        phase: rand() * TAU,
        flap: 7 + rand() * 4,
      });
    }
    roosts.push({ index: apex, x: c.x, y: c.y, bats });
  }

  // A bat is seen from a little way off its roost.
  const reach = Math.round(15 / ds);

  return {
    drawGround(ctx, from, to, clock, px) {
      for (const g of graves) if (g.index >= from && g.index <= to) drawGrave(ctx, g, px);
      for (const p of pumpkins) if (p.lit && p.index >= from && p.index <= to) drawGlow(ctx, p, clock);
      for (const p of pumpkins) if (p.index >= from && p.index <= to) drawPumpkin(ctx, p, clock, px);
    },
    drawOverhead(ctx, from, to, clock, px) {
      for (const r of roosts) {
        if (r.index < from - reach || r.index > to + reach) continue;
        for (const b of r.bats) drawBat(ctx, r, b, clock, px);
      }
    },
  };
}

function seedOf(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Candlelight on the ground round a lantern, flickering. */
function drawGlow(ctx: CanvasRenderingContext2D, p: Pumpkin, clock: number): void {
  const f = 0.8 + 0.2 * Math.sin(clock * 11 + p.phase) * Math.sin(clock * 6.7 + p.phase * 2);
  const size = 4.2;
  const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, size);
  g.addColorStop(0, `rgba(255,170,60,${(0.42 * f).toFixed(3)})`);
  g.addColorStop(0.35, `rgba(255,120,30,${(0.16 * f).toFixed(3)})`);
  g.addColorStop(1, 'rgba(255,110,20,0)');
  ctx.fillStyle = g;
  ctx.fillRect(p.x - size, p.y - size, size * 2, size * 2);
}

/** A pumpkin from above: a ribbed disc and its stem; a lantern's lid lit round its cut. */
function drawPumpkin(ctx: CanvasRenderingContext2D, p: Pumpkin, clock: number, px: number): void {
  const r = p.r;
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.beginPath();
  ctx.arc(p.x + r * 0.25, p.y + r * 0.3, r, 0, TAU);
  ctx.fill();

  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  ctx.fillStyle = PUMPKIN;
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = px * 1.5;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, TAU);
  ctx.fill();
  ctx.stroke();

  ctx.strokeStyle = PUMPKIN_GROOVE;
  ctx.lineWidth = r * 0.09;
  ctx.beginPath();
  for (let k = 0; k < 8; k++) {
    const a = (k / 8) * TAU;
    ctx.moveTo(cos(a) * r * 0.3, sin(a) * r * 0.3);
    ctx.lineTo(cos(a) * r * 0.9, sin(a) * r * 0.9);
  }
  ctx.stroke();

  if (p.lit) {
    const f = 0.75 + 0.25 * Math.sin(clock * 13 + p.phase);
    ctx.strokeStyle = LIGHT;
    ctx.globalAlpha = f;
    ctx.lineWidth = Math.max(px * 1.5, r * 0.1);
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.45, 0, TAU);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  ctx.fillStyle = STEM;
  ctx.fillRect(-r * 0.13, -r * 0.13, r * 0.26, r * 0.26);
  ctx.restore();
}

/** A grave from above: an earth mound, and its headstone at the far end. */
function drawGrave(ctx: CanvasRenderingContext2D, g: Grave, px: number): void {
  ctx.save();
  ctx.translate(g.x, g.y);
  ctx.rotate(g.rot);
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.fillRect(-0.75, -0.3, 2.05, 1.0);

  ctx.fillStyle = EARTH;
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = px * 1.5;
  ctx.beginPath();
  ctx.ellipse(0, 0, 0.95, 0.45, 0, 0, TAU);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = STONE;
  ctx.beginPath();
  if (g.rounded) {
    ctx.moveTo(0.95, -0.42);
    ctx.lineTo(1.12, -0.42);
    ctx.arc(1.12, 0, 0.42, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(0.95, 0.42);
    ctx.closePath();
  } else {
    ctx.rect(0.95, -0.45, 0.28, 0.9);
  }
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = STONE_TOP;
  ctx.beginPath();
  ctx.moveTo(1.0, -0.3);
  ctx.lineTo(1.0, 0.3);
  ctx.stroke();
  ctx.restore();
}

/** Where a bat's nose is, forward of its middle, in half-spans. */
const BAT_NOSE = 0.24;
/**
 * One side of a bat from above, nose to tail, in half-spans (x forward, y out
 * along the wing): ear, shoulder, the leading edge out to the tip, then the
 * trailing edge scalloped between the finger bones, back to the tail. The
 * other side is its mirror. Each point is [x, y], or [x, y, cx, cy] for a
 * curve with that control point.
 */
const BAT_HALF: [number, number, number?, number?][] = [
  [0.2, 0.08],
  [0.11, 0.06],
  [0.08, 0.12],
  [0.24, 1, 0.34, 0.5],
  [-0.1, 0.8, 0.04, 0.82],
  [-0.24, 0.5, -0.08, 0.56],
  [-0.3, 0.16, -0.16, 0.3],
  [-0.22, 0.06],
  [-0.3, 0],
];

/** A bat seen from above, wings beating, on its circuit of the roost. */
function drawBat(ctx: CanvasRenderingContext2D, roost: Roost, b: Bat, clock: number, px: number): void {
  const a = b.phase + b.speed * clock;
  const wobble = 1 + 0.15 * Math.sin(clock * 0.8 + b.phase);
  const x = roost.x + Math.cos(a) * b.radius * wobble;
  const y = roost.y + Math.sin(a) * b.radius * wobble;
  // Flying round the circle: the heading is a quarter turn on from the angle.
  const heading = a + (Math.sign(b.speed) * Math.PI) / 2;
  // From above, wings raised look shorter: the span narrows on the upstroke.
  const beat = 0.5 + 0.5 * Math.abs(Math.sin(clock * b.flap + b.phase));
  const span = 3.2;

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(heading);
  // BAT_HALF is in half-spans; the beat narrows it across, not along.
  const sx = span / 2;
  const sy = (span / 2) * beat;
  ctx.beginPath();
  ctx.moveTo(BAT_NOSE * sx, 0);
  for (const [hx, hy, cx, cy] of BAT_HALF) {
    if (cx === undefined || cy === undefined) ctx.lineTo(hx * sx, hy * sy);
    else ctx.quadraticCurveTo(cx * sx, cy * sy, hx * sx, hy * sy);
  }
  // Back up the other side: each segment reversed, to the point before it.
  for (let i = BAT_HALF.length - 1; i >= 0; i--) {
    const [, , cx, cy] = BAT_HALF[i];
    const [toX, toY] = i === 0 ? [BAT_NOSE, 0] : BAT_HALF[i - 1];
    if (cx === undefined || cy === undefined) ctx.lineTo(toX * sx, -toY * sy);
    else ctx.quadraticCurveTo(cx * sx, -cy * sy, toX * sx, -toY * sy);
  }
  ctx.closePath();
  ctx.fillStyle = BAT;
  ctx.fill();
  ctx.strokeStyle = BAT_RIM;
  ctx.lineWidth = px * 1.5;
  ctx.stroke();
  ctx.restore();
}
